import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@apollo/client';
import { BreadcrumbLink, Breadcrumbs, ContainerTab, ContainerTabs, NotificationBadge, Typography, } from '@guildeducationinc/recess';
import { BudgetProgress } from '@src/components/BudgetProgress';
import { ApprovalsList } from '@src/components/Manager/ApprovalsList';
import { APPROVAL_COUNTS } from '@src/graphql/queries/approvals';
import { HAS_BUDGET_POLICY } from '@src/graphql/queries/rule';
import { useAuth } from '@src/hooks/useAuth';
import { WorkflowType } from '@src/types';
import { useLocale, useSessionState } from 'pe-core-frontend';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import styles from './ApprovalTasks.module.scss';
export const ApprovalTasks = () => {
    const { t } = useLocale();
    const { currentUser } = useAuth();
    const [activeTab, setActiveTab] = useSessionState('approvalTasksActiveTab', 0);
    const { data: budgetData } = useQuery(HAS_BUDGET_POLICY);
    const hasBudgetPolicy = budgetData?.hasBudgetPolicy ?? false;
    const { data } = useQuery(APPROVAL_COUNTS, {
        variables: {
            spec: {
                clientId: currentUser?.clientId,
                onlyPending: true,
            },
        },
        skip: !currentUser?.clientId,
    });
    const counts = useMemo(() => {
        const countMap = {};
        data?.approvalCounts?.forEach(item => {
            // Convert GraphQL enum name (BUDGET_APPROVAL) to enum value (budget_approval)
            const workflowType = item.workflowType.toLowerCase();
            countMap[workflowType] = item.count;
        });
        return countMap;
    }, [data]);
    const budgetCount = counts[WorkflowType.BUDGET_APPROVAL] || 0;
    const paymentCount = (counts[WorkflowType.PAYMENT_REQUEST_APPROVAL] || 0) +
        (counts[WorkflowType.PREIMBURSEMENT_APPROVAL] || 0);
    const programCount = (counts[WorkflowType.PROGRAM_APPROVAL] || 0) +
        (counts[WorkflowType.OTHER_SCHOOL_APPROVAL] || 0);
    const tabs = useMemo(() => {
        const items = [];
        if (hasBudgetPolicy) {
            items.push({
                title: (_jsxs("div", { className: 'flex gap-2', children: ["Budgets & Funding", budgetCount > 0 && (_jsxs(_Fragment, { children: [` (${budgetCount})`, " ", _jsx(NotificationBadge, {})] }))] })),
                content: (_jsxs(_Fragment, { children: [_jsx("div", { className: 'mb-6', children: _jsx(BudgetProgress, { clientId: currentUser?.clientId, description: 'Budget is calculated across all budget policies.' }) }), _jsx(ApprovalsList, { title: 'Budgets & Funding', workflowTypes: [WorkflowType.BUDGET_APPROVAL] })] })),
            });
        }
        items.push({
            title: (_jsxs("div", { className: 'flex gap-2', children: ["Reimbursements & Advance Payments", paymentCount > 0 && (_jsxs(_Fragment, { children: [` (${paymentCount})`, " ", _jsx(NotificationBadge, {})] }))] })),
            content: (_jsx(ApprovalsList, { title: 'Reimbursements & Advance Payments', workflowTypes: [
                    WorkflowType.PAYMENT_REQUEST_APPROVAL,
                    WorkflowType.PREIMBURSEMENT_APPROVAL,
                ] })),
        });
        items.push({
            title: (_jsxs("div", { className: 'flex gap-2', children: ["Programs & Eligibility", programCount > 0 && (_jsxs(_Fragment, { children: [` (${programCount})`, " ", _jsx(NotificationBadge, {})] }))] })),
            content: (_jsx(ApprovalsList, { title: 'Programs & Eligibility', workflowTypes: [WorkflowType.PROGRAM_APPROVAL, WorkflowType.OTHER_SCHOOL_APPROVAL] })),
        });
        return items;
    }, [hasBudgetPolicy, budgetCount, paymentCount, programCount, currentUser?.clientId]);
    return (_jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.heading, children: [_jsxs(Breadcrumbs, { className: styles.breadcrumbs, children: [_jsx(BreadcrumbLink, { component: Link, to: '/', children: t('Home') }), _jsx(BreadcrumbLink, { current: true, children: "Approvals" })] }), _jsx(Typography, { variant: 'h1', children: "Approvals" })] }), _jsx(ContainerTabs, { className: styles.tabs, label: 'Approvals', activeIndex: activeTab, onActive: setActiveTab, children: tabs.map((tab, index) => (_jsx(ContainerTab, { title: tab.title, children: tab.content }, index))) })] }));
};
